import React, {useEffect, useRef, useState} from 'react';
import {AgGridReact, AgGridReactProps} from 'ag-grid-react';
import {getApi} from '../apis/backendApis';
import {
    AgPromise,
    ColumnApi,
    GridApi,
    GridReadyEvent,
    IDatasource,
    IDoesFilterPassParams,
    IFilterComp,
    IFilterParams,
    IGetRowsParams,
    RowNode
} from 'ag-grid-community';
import {CButton, CDropdown, CDropdownItem, CDropdownMenu, CDropdownToggle, CRow} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import GridAction, {ActionProps} from './gridAction';
import OperatorFloatingFilter from './operatorFloatingFilter';
import enumDescription from "../services/enumDescription";
import useLoading from "../services/useLoading";
import useAlert from "../services/useAlert";
import {
    buildColumns,
    buildFilename,
    buildMatrix,
    downloadCsv,
    downloadXlsx,
    ExportFormat,
    fetchAllRows
} from "../services/gridExport";

interface Filter {
    name:string,
    op:string
    v:string,
    v2?:string,
}

interface PaginationConf{
    page?:number,
    limit:number,
    start:number,
    filter?:string,
    sort?:string
}

interface GridProps {
    rota      : string
    handleGridReady  ?: Function
    actions  ?: Array<ActionProps>
    children ?: any,
    title ?: string
}

type Props = GridProps &  AgGridReactProps;

interface ActiveFilter {
    field: string;
    headerName: string;
    opLabel: string;
    value: string;
}

const FILTER_OP_LABELS: {[key: string]: string} = {
    contains: 'contém',
    notContains: 'não contém',
    equals: 'igual a',
    notEqual: 'diferente de',
    greaterThan: 'maior que',
    greaterThanOrEqual: 'maior ou igual a',
    lessThan: 'menor que',
    lessThanOrEqual: 'menor ou igual a',
    startsWith: 'inicia com',
    endsWith: 'termina com',
    inRange: 'entre',
    in: 'é',
};

const formatFilterDate = (value: any): string => {
    const datePart = String(value).substring(0, 10);
    const parts = datePart.split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return datePart;
};

const describeFilterCondition = (condition: any): {opLabel: string, value: string} => {
    if (!condition) {
        return {opLabel: '', value: ''};
    }
    const opLabel = FILTER_OP_LABELS[condition.type] || '';
    let value = '';
    if (condition.type === 'inRange') {
        if (condition.dateFrom != null) {
            value = `${formatFilterDate(condition.dateFrom)} e ${formatFilterDate(condition.dateTo)}`;
        } else {
            value = `${condition.filter} e ${condition.filterTo}`;
        }
    } else if (condition.dateFrom != null) {
        value = formatFilterDate(condition.dateFrom);
    } else if (condition.filter != null) {
        value = String(condition.filter);
    }
    return {opLabel, value};
};

const Grid = (props:Props) => {
    const [gridApi, setGridApi] = useState<GridApi>();
    const [gridColumnApi, setGridColumnApi] = useState<ColumnApi>();
    const [selectedRows, setSelectedRow] = useState<any[]>([]);
    const [actions, setActions] = useState<any[]>([]);
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
    const [setLoading] = useLoading();
    const [showAlert] = useAlert();

    const getHeaderName = (field: string, columnApi?: ColumnApi): string => {
        const column = columnApi?.getColumn(field) || gridColumnApi?.getColumn(field);
        const headerFromColumn = column?.getColDef()?.headerName;
        if (headerFromColumn) {
            return headerFromColumn;
        }
        let headerFromChildren = '';
        React.Children.forEach(props.children, (child) => {
            if (React.isValidElement(child)) {
                const childProps = child.props as {[key: string]: any};
                if (childProps.field === field && childProps.headerName) {
                    headerFromChildren = childProps.headerName;
                }
            }
        });
        return headerFromChildren || field;
    };

    const updateActiveFilters = (api?: GridApi, columnApi?: ColumnApi) => {
        const currentApi = api || gridApi;
        if (!currentApi) {
            setActiveFilters([]);
            return;
        }
        const model = currentApi.getFilterModel() || {};
        const list: ActiveFilter[] = [];
        Object.keys(model).forEach((field) => {
            const filterModel = model[field];
            const headerName = getHeaderName(field, columnApi);
            let opLabel = '';
            let value = '';
            if (filterModel && filterModel.type === 'in') {
                opLabel = 'é';
                const instance: any = currentApi.getFilterInstance(field);
                if (instance && typeof instance.getModelAsString === 'function') {
                    value = instance.getModelAsString();
                } else if (filterModel.filter != null) {
                    value = String(filterModel.filter);
                }
            } else if (filterModel && filterModel.operator) {
                const condition1 = describeFilterCondition(filterModel.condition1);
                const condition2 = describeFilterCondition(filterModel.condition2);
                const joiner = filterModel.operator === 'OR' ? 'ou' : 'e';
                value = `${condition1.opLabel} ${condition1.value} ${joiner} ${condition2.opLabel} ${condition2.value}`
                    .replace(/\s+/g, ' ').trim();
            } else {
                const described = describeFilterCondition(filterModel);
                opLabel = described.opLabel;
                value = described.value;
            }
            if (value !== '' && value != null) {
                list.push({field, headerName, opLabel, value});
            }
        });
        setActiveFilters(list);
    };

    const handleFilterChanged = (event: any) => {
        if (typeof (props as any).onFilterChanged === 'function') {
            (props as any).onFilterChanged(event);
        }
        updateActiveFilters(event?.api, event?.columnApi);
    };

    const removeFilter = (field: string) => {
        if (!gridApi) {
            return;
        }
        const instance: any = gridApi.getFilterInstance(field);
        if (instance) {
            instance.setModel(null);
            gridApi.onFilterChanged();
        }
    };

    const onGridReady = (params:GridReadyEvent)=>{
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);

        const dataSource = createDataSource();
        gridApi?.setDatasource(dataSource);
    }

    const refresh = () => {
        gridApi?.onFilterChanged();
    }

    const clearFilters = () => {
        gridApi?.setFilterModel({});
        localStorage.removeItem('filterModelData');
    }

    /**
     * Exporta o resultado inteiro da consulta (não só a página visível), com os
     * filtros e a ordenação atuais, usando as próprias colunas da tela como
     * cabeçalho e formatação. Refs: #474976
     */
    const handleExport = (format: ExportFormat) => {
        if (!gridApi || !gridColumnApi) {
            return;
        }

        const columns = buildColumns(gridColumnApi.getAllDisplayedColumns(), gridApi, gridColumnApi);
        if (columns.length === 0) {
            showAlert({
                title: 'Erro',
                msg: 'Não há colunas para exportar.'
            });
            return;
        }

        const filters = getFilters();
        const filter = filters.length > 0 ? JSON.stringify(filters) : undefined;
        const sortModel = getSortModel();
        const sort = sortModel.length > 0 ? JSON.stringify(sortModel) : undefined;
        const filename = buildFilename(props.rota, format, props.title);

        setLoading(true);
        fetchAllRows(props.rota, filter, sort)
            .then((rows) => {
                const matrix = buildMatrix(columns, rows);
                return format === 'xlsx'
                    ? downloadXlsx(matrix, filename)
                    : downloadCsv(matrix, filename);
            })
            .catch(() => {
                showAlert({
                    title: 'Erro',
                    msg: 'Não foi possível exportar os dados.'
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }
    
    useEffect(()=>{
        if(gridApi){
            const rotaAtual = props.rota;
            const goBack = localStorage.getItem('goBack');
            const rotaLocalStorage = localStorage.getItem('rota');
            if (rotaLocalStorage === rotaAtual && goBack === 'true') {
                applyStoredFilters(gridApi);
            } else if (props.handleGridReady) {
                props.handleGridReady(gridApi);
            }
        }
        const dataSource = createDataSource();
        gridApi?.setDatasource(dataSource);
    }, [gridApi])

    const applyStoredFilters = (api: any) => {
        const filtersJSON = localStorage.getItem('filterModelData');
        if (filtersJSON) {
            const filters = JSON.parse(filtersJSON);
            Object.keys(filters).forEach(key => {
                const filterInstance = api?.getFilterInstance(key);
                if (filters[key]?.type === 'in') {
                    filters[key].value = filters[key]?.filter?.split(',');
                }
                filterInstance?.setModel(filters[key]);
            });
            setTimeout(() => updateActiveFilters(api), 0);
        }
    };

    const storeFilters = () => {
        if (gridApi) {
            const filterModelData = gridApi.getFilterModel();
            localStorage.setItem('filterModelData', JSON.stringify(filterModelData));
            localStorage.setItem('rota', props.rota);
        }
    };

    const getSortModel = () => {
        const sortModel = (gridColumnApi?.getColumnState() || []).filter((c: any) => c.sort);
        return sortModel.map((data: any) => ({col: data.colId, sort: data.sort}));
    }

    const getFilters = () => {
        let filters: Array<Filter> = [];
        if (gridApi?.getFilterModel()) {
            let filterModelData = gridApi?.getFilterModel();
            if (localStorage.getItem('goBack') === 'false') {
                storeFilters();
            }
            for (let filterName in filterModelData) {
                let filterModel = filterModelData[filterName];
                let operador = '=';
                switch (filterModel.type) {
                    case 'contains':
                        operador = 'like';
                        break;
                    case 'lessThan':
                        operador = '<';
                        break;
                    case 'lessThanOrEqual':
                        operador = '<=';
                        break;
                    case 'greaterThan':
                        operador = '>';
                        break;
                    case 'greaterThanOrEqual':
                        operador = '>=';
                        break;
                    case 'notContains':
                        operador = 'notlike';
                        break;
                    case 'equals':
                        operador = '=';
                        break;
                    case 'notEqual':
                        operador = '<>';
                        break;
                    case 'startWith':
                        operador = 'inicialike';
                        break;
                    case 'endsWith':
                        operador = 'terminalike';
                        break;
                    case 'inRange':
                        operador = 'between';
                        break;
                    case 'in':
                        operador = 'contains';
                        break;
                }

                let val = filterModel.filter;
                if (val == null && filterModel.type !== 'in') {
                    val = filterModel
                }
                // Skip 'in' filters with no value (e.g. after clear)
                if (filterModel.type === 'in' && (val == null || val === '')) {
                    continue;
                }
                let val2 = null;

                if (filterModel.dateFrom) {
                    val = filterModel.dateFrom;
                }

                if (filterModel.dateTo) {
                    val2 = filterModel.dateTo;
                }

                if (filterModel.filterTo) {
                    val2 = filterModel.filterTo;
                }

                let param: Filter = {
                    "name": filterName,
                    "v": val,
                    "v2": val2,
                    "op": operador
                };

                filters.push(param);
            }
        }
        return filters;
    }

    const createDataSource = () =>{
        const dataSource: IDatasource = {

            getRows: (params: IGetRowsParams) => {
                gridApi?.showLoadingOverlay();
                let page = gridApi?.paginationGetCurrentPage();
                if(page){
                    page++;
                }
                let sortData = [];
                let sorts = 0;
                for(let row in params.sortModel){                    
                    let data = params.sortModel[row];
                    sortData.push ({col:data.colId, sort:data.sort});
                    sorts++;
                }
                let filters = getFilters();
                localStorage.setItem('goBack', 'false');
                let filter, sort;
                if(filters.length > 0){
                    filter = JSON.stringify(filters);
                }              
                if(sortData && sorts > 0){
                    sort = JSON.stringify(sortData);
                }
                let config:PaginationConf = {
                    filter,
                    page,
                    limit:20,
                    start:params.startRow,
                    sort
                };
                let endPoint = props.rota;
                let api = getApi();
                api.get(endPoint, {params: config}).then(response=>{
                    gridApi?.hideOverlay();
                    params.successCallback(
                        response.data.rows,
                        response.data.total
                    );
                }).catch((error)=>{
                    gridApi?.hideOverlay();
                    params.failCallback();
                    showAlert({
                        title:'Erro',
                        msg: error.response.data.error,
                        details:error.response.data.trace,
                    });
                });
            }
        };

        return dataSource;
    }

    useEffect(()=>{
        if(gridApi){
            const currentGridApi = gridApi;
            let newActions = props.actions?.map((action)=>{
                if(!action.onClickFunction && ['delete', 'post'].includes(action.type)){
                    action.onClickFunction = (rows, route)=>{
                        setLoading(true);
                        let api = getApi();
                        let id = rows[0].id;
                        let method:'post'|'delete' = 'post';
                        let url = route+`/${id}`;
                        if(action.type == 'delete'){
                            method = 'delete';
                        }
                        let callbackFn = action.callBackFunction;
                        api.request(
                            {
                                method,
                                url
                            }
                        ).then((response)=>{
                                const data = response.data ?? {};
                                let toaster = !(data?.toaster === false);
                                showAlert({
                                    msg: data.msg ?? 'Operação realizada com sucesso',
                                    toaster: toaster,
                                    fnOk: () => {
                                        if(callbackFn){
                                            let isRefresh = callbackFn(response);
                                            if (isRefresh){
                                                currentGridApi.onFilterChanged();
                                            }
                                        }
                                    }
                                });

                            }).catch((error)=>{
                                const errData = error?.response?.data ?? {};
                                showAlert({
                                    title:'Erro',
                                    msg: errData.error ?? 'Erro ao processar a requisição',
                                    details: errData.trace,
                                    fnOk: () => {
                                        if(callbackFn){
                                            let isRefresh = callbackFn(error);
                                            if (isRefresh){
                                                currentGridApi.onFilterChanged();
                                            }
                                        }
                                    }
                                });
                        }).finally(()=>{
                            currentGridApi.setDatasource(createDataSource());
                            setLoading(false);
                        });

                    }
                }
                return action
            });
            if(newActions){
                setActions(newActions);
            }
        }
    }, [gridApi])

    const onSelectionChanged = ()=>{
        if(gridApi){
            let localSelectedRows = gridApi.getSelectedRows();
            setSelectedRow(localSelectedRows);
        }
    }

    const disableAction = (action:ActionProps)=>{
        let disableAction = action.type != 'normal' && action.type != 'custom' && !(selectedRows && selectedRows.length > 0 );

        if(!disableAction && action.validate){
            disableAction = !action.validate(selectedRows);
        }

        return disableAction;
    }

    const enhancedChildren = React.Children.map(props.children, (child) => {
        if (!React.isValidElement(child)) {
            return child;
        }
        const childProps = child.props as {[key: string]: any};
        const filter = childProps.filter !== undefined
            ? childProps.filter
            : (props.defaultColDef && (props.defaultColDef as {[key: string]: any}).filter);
        const isFilterable = filter !== undefined && filter !== false && filter !== null;
        const isEnum = typeof filter === 'function';
        const optedOut = childProps.floatingFilterComponent || childProps.floatingFilter === false;
        if (!isFilterable || isEnum || optedOut) {
            return child;
        }
        return React.cloneElement(child, {
            floatingFilterComponent: OperatorFloatingFilter,
            floatingFilterComponentParams: {
                ...(childProps.floatingFilterComponentParams || {}),
                suppressFilterButton: true,
            },
        } as any);
    });

    // Evita recriar columnDefs a cada render (o ag-grid 31 reaplica as colunas e perde o estado de filtro/ordenacao)
    const columnDefsCache = useRef<{key: string, defs: any[]}>({key: "", defs: []});
    const nextColumnDefs = (enhancedChildren || []).filter((c: any) => React.isValidElement(c)).map((c: any) => ({...c.props}));
    const columnDefsKey = nextColumnDefs.map((d: any) => [d.field, d.headerName, d.width, d.flex, typeof d.filter === "function" ? d.filter.name : String(d.filter)].join("|")).join(";");
    if (columnDefsCache.current.key !== columnDefsKey) {
        columnDefsCache.current = {key: columnDefsKey, defs: nextColumnDefs};
    }
    const stableColumnDefs = columnDefsCache.current.defs;

    return (
        <div className="ag-theme-alpine lg-12 ag-grid-padrao" style={{position: 'relative'}}>
            {props.title && props.title.length > 0 &&
            <CRow className={'d-flex mx-0 px-3 align-items-center'} style={{paddingTop: 8, paddingBottom: 8}}>
                <div className={'flex-grow-1 text-left font-lg alert alert-info mb-0'}>{props.title}</div>
            </CRow>}
            <CRow className="d-flex mx-0 align-items-center" style={{paddingTop: 0, paddingBottom: 6, paddingLeft: 0, paddingRight: 0, borderBottom: '1px solid #e2e8f0'}}>
                <div className={'flex-grow-1'} style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                    {actions && actions.map(action=>(
                            <GridAction disabled={disableAction(action)} key={action.route} gridApi={gridApi} size="sm" className="m-0" {...action}>{action.title}</GridAction>
                        )
                    )
                    }
                </div>
                <div style={{display: 'flex', gap: 4}}>
                    <CButton size="sm" className={'btn-secondary'} onClick={clearFilters}>Limpar Filtros</CButton>
                    <CButton size="sm" className={'btn-info'} onClick={refresh}>Atualizar</CButton>
                </div>
            </CRow>
            {activeFilters.length > 0 &&
            <div className="d-flex flex-wrap align-items-center px-2 pb-2" style={{gap: 6}}>
                <span style={{fontSize: 12, color: '#5c6873', fontWeight: 600, marginRight: 2}}>Filtrando por:</span>
                {activeFilters.map((filter) => (
                    <span
                        key={filter.field}
                        title={`${filter.headerName} ${filter.opLabel} ${filter.value}`}
                        style={{
                            display: 'inline-flex', alignItems: 'center', maxWidth: 320,
                            background: '#ebf3ff', border: '1px solid #b6d4fe', color: '#084298',
                            borderRadius: 12, padding: '1px 6px 1px 10px', fontSize: 12, lineHeight: 1.6,
                        }}
                    >
                        <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {filter.headerName} <span style={{opacity: 0.7}}>{filter.opLabel}</span> <b>{filter.value}</b>
                        </span>
                        <span
                            onClick={() => removeFilter(filter.field)}
                            title="Remover filtro"
                            style={{cursor: 'pointer', marginLeft: 6, fontWeight: 700, fontSize: 15, lineHeight: 1, flex: '0 0 auto'}}
                        >×</span>
                    </span>
                ))}
            </div>}
            <AgGridReact
                {...props} children={undefined as any}
                columnDefs={stableColumnDefs}
                defaultColDef={{ floatingFilter: true, ...(props.defaultColDef || {}) }}
                pagination={true}
                paginationPageSize={20}
                rowModelType={'infinite'}
                cacheBlockSize={20}
                onGridReady={onGridReady}
                onSelectionChanged={onSelectionChanged}
                onFilterChanged={handleFilterChanged}
                rowSelection={'single'}
                headerHeight={36}
                localeText= {{
                    // for number filter and text filter
                    notEqual: 'Diferente',
                    filterOoo: 'Filtro...',
                    lessThan: 'Menor',
                    greaterThan: 'Maior',
                    lessThanOrEqual: 'Menor ou igual',
                    greaterThanOrEqual: 'Maior ou igual',
                    inRange: 'No intervalo',
                    inRangeStart: 'De',
                    inRangeEnd: 'Até',
                    equals: 'Igual',
                    contains: 'Contém',
                    notContains: 'Não Contém',
                    startsWith: 'Inicia com',
                    endsWith: 'Termina com',
                    // filter conditions
                    andCondition: 'e',
                    orCondition: 'ou',
                    //rodapé
                    page: 'página',
                    more: '',
                    to: 'até',
                    of: 'de',
                    next: 'próxima',
                    last: 'última',
                    first: 'primeira',
                    previous: 'anterior',
                    loadingOoo: 'Carregando Dados...',
                }}
            />
            {/*
              * O rodape (.ag-paging-panel, 36px) e renderizado pelo proprio ag-grid e
              * alinha o paginador a direita. A barra de exportacao e ancorada por cima
              * dele, a esquerda, para nao precisar mexer no layout flex do grid.
              */}
            <div style={{
                position: 'absolute', left: 12, bottom: 0, height: 36,
                display: 'flex', alignItems: 'center', zIndex: 1
            }}>
                <CDropdown>
                    <CDropdownToggle size="sm" className={'btn-success'} caret>
                        <CIcon name="cilCloudDownload" className="mr-1" height={14}/>
                        Exportar
                    </CDropdownToggle>
                    {/* Abre para cima: no rodape um menu para baixo sairia da tela. */}
                    <CDropdownMenu placement="top-start">
                        <CDropdownItem onClick={() => handleExport('csv')}>CSV (.csv)</CDropdownItem>
                        <CDropdownItem onClick={() => handleExport('xlsx')}>Excel (.xlsx)</CDropdownItem>
                    </CDropdownMenu>
                </CDropdown>
            </div>
        </div>
    );
};

export abstract class ListaMultiplaFilter implements IFilterComp{
    private gui: HTMLElement = document.createElement('div');
    private eFilterText: Element | null = null;
    private valueGetter?: any;
    private filterText:string | null = null;

    doesFilterPass(params: IDoesFilterPassParams): boolean {
        return true;
    }

    getGui(): HTMLElement {
        return this.gui;
    }

    getModel(): any {
        return { filter: this?.filterText, type: 'in'};
    }

    getModelAsString(): string {
        if (!this.filterText) {
            return '';
        }
        const list = this.getList();
        return this.filterText
            .split(',')
            .map((v) => list.getDescription(Number(v)) || v)
            .join(', ');
    }

    init(params: IFilterParams): AgPromise<void> | void {

        this.valueGetter = params.valueGetter;
        this.filterText = null;
        this.setupGui(params);

        return undefined;
    }

    setupGui(params:IFilterParams)  {
        this.gui =  document.createElement('div');

        let options = this.getList().getList().map(item=>(
            `<option value="${item.value}"> ${item.description}</option>`
        ));

        this.gui.innerHTML =
            '<div style="padding: 4px; width: 200px;">' +
            '<div style="font-weight: bold;">Selecione</div>' +
            '<div>' +
            '<select multiple style="width: 100%; margin: 4px 0px 4px 0px;" id="filterText">'+
                options.join('')+
            '</select>'+
            '</div>' +
            '</div>';

        this.eFilterText = this.gui.querySelector('#filterText');
        if(this.eFilterText){
            // @ts-ignore
            this.eFilterText.addEventListener("change", listener);
        }

        var that = this;

        function listener(event:React.ChangeEvent<HTMLSelectElement>) {
            let values = [];
            let selectedOptions = event.target.selectedOptions;
            for(let i = 0; i< selectedOptions.length; i++){
                let item = selectedOptions.item(i);
                if(item){
                    values.push(item.value);
                }
            }
            that.filterText = values.join(',');
            params.filterChangedCallback();
        }
    }

    isFilterActive(): boolean {
        return this.filterText != null && this.filterText !== '';
    }

    setModel(model: any): void | AgPromise<void> {
        // Deselect all options first
        const allOptions = this.gui.querySelectorAll('#filterText option');
        allOptions.forEach((opt: any) => { opt.selected = false; });
        this.filterText = null;

        let options: number[] = model?.value;
        if(options && options.length>0){
            options.map(option=>{
                let optionElement = this.gui.querySelector("#filterText option[value='" + option + "']");
                if(optionElement){
                    // @ts-ignore
                     optionElement.selected = true;

                }
            })

            this.filterText = options.join(',');
        }
    }

    abstract getList() : enumDescription
}

export default Grid;